import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  BusinessAccount,
  BusinessAccountLimits,
  ServiceAccount,
  Employee,
  EmployeeRole,
  FinancialAccountListItem,
  FilterValues,
  FinancialAccountDetails,
  AccountBalancesRaw,
  Beneficiaries,
  Beneficiary,
} from '@shared/models';
import { BusinessAcccountActions, EmployeeActions, BeneficiaryActions } from './business-account.actions';

export interface BusinessAccountState {
  businessAccountDetails: BusinessAccount | null;
  businessAccountBalances: AccountBalancesRaw | null;
  businessAccountLimits: BusinessAccountLimits | null;
  businessAccountLimitsCurrency: string;
  businessAccountLimitsLoading: boolean;
  serviceAccountDetails: ServiceAccount | null;
  employeeDetails: Employee | null;
  employeeRoles: EmployeeRole[];
  businessFinancialAccounts: FinancialAccountListItem[];
  businessFinancialAccountsTotalElements: number;
  businessFinancialAccountsActiveFilters: FilterValues | null;
  isBusinessFinancialAccountsLoading: boolean;
  escrowFinancialAccount: FinancialAccountDetails | null;
  beneficiaries: Beneficiaries[];
  beneficiariesLoading: boolean;
  beneficiaryDetails: Beneficiary | null;
  beneficiaryDetailsLoading: boolean;
}

export const businessAccountInitialState: BusinessAccountState = {
  businessAccountDetails: null,
  businessAccountBalances: null,
  businessAccountLimits: null,
  businessAccountLimitsCurrency: 'USD',
  businessAccountLimitsLoading: false,
  serviceAccountDetails: null,
  employeeDetails: null,
  employeeRoles: [],
  businessFinancialAccounts: [],
  businessFinancialAccountsTotalElements: 0,
  businessFinancialAccountsActiveFilters: null,
  isBusinessFinancialAccountsLoading: false,
  escrowFinancialAccount: null,
  beneficiaries: [],
  beneficiariesLoading: false,
  beneficiaryDetails: null,
  beneficiaryDetailsLoading: false,
};

export const businessAccountFeature = createFeature({
  name: 'businessAccount',
  reducer: createReducer(
    businessAccountInitialState,
    on(
      BusinessAcccountActions.loadBusinessAccountDetails,
      (state): BusinessAccountState => ({
        ...state,
        businessAccountDetails: null,
        businessAccountLimits: null,
        businessAccountLimitsLoading: true,
        businessAccountBalances: null,
      }),
    ),
    on(
      BusinessAcccountActions.loadBusinessAccountDetailsSuccess,
      (state, { businessAccount }): BusinessAccountState => ({ ...state, businessAccountDetails: businessAccount }),
    ),
    on(
      BusinessAcccountActions.loadBusinessAccountLimitsSuccess,
      (state, { businessAccountLimits }): BusinessAccountState => ({
        ...state,
        businessAccountLimits,
        businessAccountLimitsLoading: false,
      }),
    ),
    on(
      BusinessAcccountActions.loadBusinessAccountLimitsFailure,
      (state): BusinessAccountState => ({
        ...state,
        businessAccountLimits: null,
        businessAccountLimitsLoading: false,
      }),
    ),
    on(
      BusinessAcccountActions.setLimitsCurrency,
      (state, { currency }): BusinessAccountState => ({ ...state, businessAccountLimitsCurrency: currency }),
    ),
    on(
      BusinessAcccountActions.loadBusinessAccountBalancesSuccess,
      (state, { businessAccountBalances }): BusinessAccountState => ({ ...state, businessAccountBalances }),
    ),
    on(
      BusinessAcccountActions.loadServiceAccountDetails,
      (state): BusinessAccountState => ({
        ...state,
        serviceAccountDetails: null,
      }),
    ),
    on(
      BusinessAcccountActions.loadServiceAccountDetailsSuccess,
      (state, { serviceAccount }): BusinessAccountState => ({ ...state, serviceAccountDetails: serviceAccount }),
    ),
    on(BusinessAcccountActions.deleteServiceAccountSuccess, (state): BusinessAccountState => ({ ...state, serviceAccountDetails: null })),
    on(
      BusinessAcccountActions.loadBusinessFinancialAccounts,
      (state): BusinessAccountState => ({
        ...state,
        businessFinancialAccounts: [],
        businessFinancialAccountsTotalElements: 0,
        isBusinessFinancialAccountsLoading: true,
      }),
    ),
    on(
      BusinessAcccountActions.loadBusinessFinancialAccountsSuccess,
      (state, { financialAccounts, totalElements }): BusinessAccountState => {
        return {
          ...state,
          businessFinancialAccounts: financialAccounts,
          businessFinancialAccountsTotalElements: totalElements,
          isBusinessFinancialAccountsLoading: false,
        };
      },
    ),
    on(BusinessAcccountActions.loadBusinessFinancialAccountsFailure, (state): BusinessAccountState => {
      return {
        ...state,
        businessFinancialAccounts: [],
        businessFinancialAccountsTotalElements: 0,
        isBusinessFinancialAccountsLoading: false,
      };
    }),
    on(BusinessAcccountActions.setBusinessFinancialAccountsActiveFilters, (state, { filters }): BusinessAccountState => {
      return { ...state, businessFinancialAccountsActiveFilters: filters };
    }),
    on(BusinessAcccountActions.loadEscrowFinancialAccount, (state): BusinessAccountState => ({ ...state, escrowFinancialAccount: null })),
    on(
      BusinessAcccountActions.loadEscrowFinancialAccountSuccess,
      (state, { escrowFinancialAccount }): BusinessAccountState => ({ ...state, escrowFinancialAccount }),
    ),
    on(
      BusinessAcccountActions.loadBeneficiaries,
      (state): BusinessAccountState => ({ ...state, beneficiaries: [], beneficiariesLoading: true }),
    ),
    on(
      BusinessAcccountActions.loadBeneficiariesSuccess,
      (state, { beneficiaries }): BusinessAccountState => ({ ...state, beneficiaries, beneficiariesLoading: false }),
    ),
    on(
      BusinessAcccountActions.loadBeneficiariesFailure,
      (state): BusinessAccountState => ({ ...state, beneficiaries: [], beneficiariesLoading: false }),
    ),
    on(EmployeeActions.loadEmployeeDetails, (state): BusinessAccountState => ({ ...state, employeeDetails: null })),
    on(EmployeeActions.loadEmployeeDetailsSuccess, (state, { employeeDetails }): BusinessAccountState => ({ ...state, employeeDetails })),
    on(EmployeeActions.deleteEmployeeSuccess, (state): BusinessAccountState => ({ ...state, employeeDetails: null })),
    on(EmployeeActions.loadEmployeeRoles, (state): BusinessAccountState => ({ ...state, employeeRoles: [] })),
    on(EmployeeActions.loadEmployeeRolesSuccess, (state, { employeeRoles }): BusinessAccountState => ({ ...state, employeeRoles })),
    on(
      BeneficiaryActions.loadBeneficiaryDetails,
      (state): BusinessAccountState => ({ ...state, beneficiaryDetails: null, beneficiaryDetailsLoading: true }),
    ),
    on(
      BeneficiaryActions.loadBeneficiaryDetailsSuccess,
      (state, { beneficiaryDetails }): BusinessAccountState => ({ ...state, beneficiaryDetails, beneficiaryDetailsLoading: false }),
    ),
    on(
      BeneficiaryActions.loadBeneficiaryDetailsFailure,
      (state): BusinessAccountState => ({ ...state, beneficiaryDetails: null, beneficiaryDetailsLoading: false }),
    ),
    on(BeneficiaryActions.deleteBeneficiarySuccess, (state): BusinessAccountState => ({ ...state, beneficiaryDetails: null })),
  ),
  extraSelectors: ({
    selectBusinessAccountDetails,
    selectBusinessAccountLimits,
    selectBusinessAccountLimitsCurrency,
    selectBusinessAccountLimitsLoading,
    selectBusinessFinancialAccountsActiveFilters,
    selectBusinessAccountBalances,
  }) => ({
    businessAccountBalanceByCurrency: createSelector(
      selectBusinessAccountBalances,
      selectBusinessAccountDetails,
      (businessAccountBalances, businessAccountDetails) => {
        return businessAccountBalances?.[businessAccountDetails?.currency ?? 'USD'];
      },
    ),
    businessAccountLimitCurrencies: createSelector(
      selectBusinessAccountLimits,
      (businessAccountLimits) => Object.keys(businessAccountLimits ?? {}) ?? [],
    ),
    businessAccountLimitsByCurrency: createSelector(
      selectBusinessAccountLimits,
      selectBusinessAccountLimitsCurrency,
      selectBusinessAccountLimitsLoading,
      (businessAccountLimits, currency, limitsLoading) => {
        return businessAccountLimits?.[currency] ?? (!limitsLoading ? [] : null);
      },
    ),
    isAdminBusinessAccount: createSelector(
      selectBusinessAccountDetails,
      (businessAccountDetails) => businessAccountDetails?.name === 'admin',
    ),
    selectBusinessFinancialAccountsFiltersRequest: createSelector(selectBusinessFinancialAccountsActiveFilters, (activeFilters) => {
      if (!activeFilters) {
        return;
      }

      const filters: Record<string, string> = {};
      Object.keys(activeFilters).map((filterKey) => {
        const value = activeFilters[filterKey];
        if (value && value !== 'all') {
          filters[filterKey] = value;
        }
      });
      return filters;
    }),
    selectBusinessFinancialAccountsActiveFiltersCount: createSelector(selectBusinessFinancialAccountsActiveFilters, (activeFilters) => {
      if (!activeFilters) {
        return 0;
      }
      return Object.values(activeFilters).filter((value) => !!value && value !== 'all').length;
    }),
  }),
});
